'use client';

import { useAuth } from '@korvo/core/client/hooks/useAuth';
import { useCurrentUserProfile } from '@korvo/core/client/hooks/useCurrentUserProfile';
import { formatChatErrorForDisplay } from '@korvo/core/lib/chat-client-error-message';
import {normalizeError } from '@korvo/core/lib/normalize-error';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { ChatComposerHandle } from '../components/ChatComposer';
import { ChatComposer } from '../components/ChatComposer';
import { ChatMessageList } from '../components/ChatMessageList';
import type { ChatSession, ChatSessionManager } from '../types/session-manager';
import type { ChatInstance } from './ChatInstance';
import { useChatInstanceManager } from './ChatInstanceManager';
import type { ChatInstanceConfig, ChatInstanceState, ChatMessage } from './types';
 

type ChatViewProps = {
    sessionId: string;
    config: ChatInstanceConfig;
    sessionManager: ChatSessionManager;
    session?: ChatSession; // Optional session data
    className?: string;
    style?: React.CSSProperties;
    onStateChange?: (state: ChatInstanceState) => void;
};
export function ChatView({ sessionId, config, sessionManager, session, className = '', style, onStateChange }: ChatViewProps) {
    const BOTTOM_THRESHOLD_PX = 120;
    const { createInstance, getInstance, getAllInstances, loadSessionIntoInstance } = useChatInstanceManager();
    const [state, setState] = useState<ChatInstanceState>({
        messages: [],
        isLoading: false,
        isStreaming: false,
        error: null,
        session: null,
        isPaused: false,
        isDestroyed: false
    });
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const lastSessionIdRef = useRef<string | null>(null);
    // Debug initial loading state
    useEffect(() => {
    }, [isInitialLoading]);
    // Hide loading immediately when fully loaded
    useEffect(() => {
        if (isInitialLoading && (!state.isLoading && !state.isStreaming)) {
            setIsInitialLoading(false);
        }
    }, [isInitialLoading, state.isLoading, state.isStreaming]);
    // When switching chats (sessionId changes), re-show the loading overlay
    useEffect(() => {
        if (lastSessionIdRef.current === null) {
            lastSessionIdRef.current = sessionId;
            return;
        }
        if (lastSessionIdRef.current !== sessionId) {
            lastSessionIdRef.current = sessionId;
            setIsInitialLoading(true);
            // No artificial delay - let the immediate hide effect handle it
        }
    }, [sessionId, state.messages.length, state.error, state.isLoading, state.isStreaming, state.session]);
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const instanceRef = useRef<ChatInstance | null>(null);
    const [isUserAtBottom, setIsUserAtBottom] = useState(true);
    const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isAtBottomRef = useRef<boolean>(true);
    const isUserScrollingRef = useRef<boolean>(false);
    const messageCountRef = useRef<number>(0);
    const hasAutoScrolledForStreamRef = useRef<boolean>(false);
    const lastAssistantMessageIdRef = useRef<string | null>(null);
    const wasStreamingRef = useRef<boolean>(false);
    const composerRef = useRef<ChatComposerHandle>(null);
    const { profile: currentUserProfile } = useCurrentUserProfile();
    const { isAdmin } = useAuth();
    const scrollToBottomAfterRender = (behavior: ScrollBehavior = 'smooth') => {
        // Ensure DOM has applied new content before measuring/scrolling
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                if (chatContainerRef.current) {
                    chatContainerRef.current.scrollTo({
                        top: chatContainerRef.current.scrollHeight,
                        behavior
                    });
                }
            });
        });
    };
    // Create or get existing instance on mount
    useEffect(() => {
        let instanceId: string;
        let instance: ChatInstance | null = null;
        // Try to get existing instance first
        const existingInstances = Array.from(getAllInstances().values());
        const existingInstance = existingInstances.find(inst => inst.getSessionId() === sessionId);
        if (existingInstance) {
            instance = existingInstance;
            instanceId = instance.getInstanceId();
        }
        else {
            // Create new instance
            instanceId = createInstance({ ...config, sessionId });
            instance = getInstance(instanceId);
        }
        if (instance) {
            instanceRef.current = instance;
            // Set up event listeners first
            const handleStateChange = (nextState: unknown) => {
                const previousMessageCount = messageCountRef.current;
                if (!nextState || typeof nextState !== 'object')
                    return;
                const typedState = nextState as ChatInstanceState;
                setState(typedState);
                void Promise.resolve(onStateChange?.(typedState));
                // Mark initial loading as complete when we get meaningful state or the session is ready and idle
                if (isInitialLoading && (typedState.messages.length > 0 ||
                    typedState.error ||
                    (!typedState.isLoading && !typedState.isStreaming))) {
                    setIsInitialLoading(false);
                }
                // Immediate scroll on streaming start if user is at bottom
                if (typedState.isStreaming && !wasStreamingRef.current && isAtBottomRef.current) {
                    scrollToBottomAfterRender('auto');
                }
                // Auto-scroll if new messages were added
                if (typedState.messages.length > previousMessageCount) {
                    const container = chatContainerRef.current;
                    if (container) {
                        const { scrollTop, scrollHeight, clientHeight } = container;
                        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
                        const atBottom = distanceFromBottom <= BOTTOM_THRESHOLD_PX;
                        // Get the last message to check if it's from user
                        const lastMessage = typedState.messages[typedState.messages.length - 1];
                        const isUserMessage = lastMessage?.role === 'user';
                        const isAssistantMessage = lastMessage?.role === 'assistant';
                        // Detect start of a new assistant message stream and reset per-stream flag
                        if (isAssistantMessage) {
                            if (lastAssistantMessageIdRef.current !== lastMessage.id) {
                                lastAssistantMessageIdRef.current = lastMessage.id;
                                hasAutoScrolledForStreamRef.current = false;
                            }
                        }
                        // Always scroll for user messages, or if user was at bottom before the update
                        // Use isAtBottomRef (pre-update) instead of recomputed atBottom to avoid missing
                        // the scroll when the new message increases scrollHeight.
                        if (isUserMessage || isAtBottomRef.current || atBottom) {
                            scrollToBottomAfterRender('smooth');
                        }
                    }
                }
                // During streaming: perform a single auto-scroll per assistant message
                // only if the user is currently at the bottom. If the user scrolls up,
                // isAtBottomRef becomes false and we won't force-scroll again.
                if (typedState.isStreaming &&
                    isAtBottomRef.current &&
                    !hasAutoScrolledForStreamRef.current) {
                    hasAutoScrolledForStreamRef.current = true;
                    requestAnimationFrame(() => {
                        if (chatContainerRef.current) {
                            chatContainerRef.current.scrollTo({
                                top: chatContainerRef.current.scrollHeight,
                                behavior: 'smooth'
                            });
                        }
                    });
                }
                // When streaming stops, allow future streams to auto-scroll once again
                if (!typedState.isStreaming) {
                    hasAutoScrolledForStreamRef.current = false;
                    // UX: focus input when assistant finished responding
                    composerRef.current?.focusInput();
                }
                // Track previous streaming flag
                wasStreamingRef.current = typedState.isStreaming;
                // update tracked message count after handling
                messageCountRef.current = typedState.messages.length;
            };
            const handleMessageAdded = (message: ChatMessage) => {
                const container = chatContainerRef.current;
                if (container) {
                    const { scrollTop, scrollHeight, clientHeight } = container;
                    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
                    const atBottom = distanceFromBottom <= BOTTOM_THRESHOLD_PX;
                    // Always scroll for user messages, or if user is/was near bottom before add
                    if (message.role === 'user' || isAtBottomRef.current || atBottom) {
                        scrollToBottomAfterRender('smooth');
                    }
                }
            };
            const handleMessageUpdated = () => {
                // Follow streaming only if user is currently at/near bottom
                if (!chatContainerRef.current)
                    return;
                if (!isAtBottomRef.current)
                    return;
                scrollToBottomAfterRender('smooth');
            };
            instance.on('state:changed', handleStateChange);
            instance.on('message:added', handleMessageAdded);
            instance.on('message:updated', handleMessageUpdated);
            // Load session data using session manager
            (async () => {
                try {
                    let currentSession: ChatSession | null | undefined = session;
                    if (!currentSession) {
                        currentSession = await sessionManager.getCurrentSession();
                    }
                    if (currentSession != null) {
                        await loadSessionIntoInstance(instanceId, { ...currentSession, threadId: currentSession.threadId ?? undefined });
                        // Let the immediate hide effect handle dismissal
                    }
                    else {
                        // Create a new session if none exists
                        const createdSession = await sessionManager.createSession();
                        await loadSessionIntoInstance(instanceId, { ...createdSession, threadId: createdSession.threadId ?? undefined });
                        // Let the immediate hide effect handle dismissal
                    }
                }
                catch (error: unknown) {
                    const errorNormalized = normalizeError(error);
                    console.errorNormalized('Failed to load session:', errorNormalized);
                    throw errorNormalized;
                }
            })().catch((error) => {
                console.error('Unhandled error in loadSession:', error);
            });
            // Resume instance if it was paused
            const paused = (instance as unknown as Record<string, unknown>).isPausedState ? ((instance as unknown as Record<string, unknown>).isPausedState as () => boolean)() : ((instance as unknown as Record<string, unknown>).isPaused as (() => boolean) | undefined)?.() || false;
            if (paused) {
                void instance.resume();
            }
            // Ensure stream is open for the current thread if we have one
            const currentThreadId = instance.getCurrentThreadId();
            if (currentThreadId && (instance as unknown as Record<string, unknown>).threadsClient) {
                void instance.openStream(currentThreadId);
            }
            // Get initial state if available
            const initialState = instance.getState();
            if (initialState && typeof initialState === 'object') {
                setState(initialState as ChatInstanceState);
            }
            return () => {
                instance?.off('state:changed', handleStateChange);
                instance?.off('message:added', handleMessageAdded);
                instance?.off('message:updated', handleMessageUpdated);
                // Don't destroy instance, just pause it for background processing
                // The stream will remain alive for background streaming
                const destroyed = (instance as unknown as Record<string, unknown>).isDestroyedState ? ((instance as unknown as Record<string, unknown>).isDestroyedState as () => boolean)() : ((instance as unknown as Record<string, unknown>).isDestroyed as (() => boolean) | undefined)?.() || false;
                if (instance && !destroyed) {
                    void (instance as unknown as { pause: () => Promise<void> }).pause();
                }
            };
        }
    }, [sessionId, sessionManager, session, createInstance, getInstance, getAllInstances, onStateChange]);
    const handleSendMessage = useCallback(async (content: string) => {
        if (!content.trim() || state.isLoading || !instanceRef.current)
            return;
        try {
            await instanceRef.current.sendMessage(content.trim(), [], config.context);
        }
        catch (err: unknown) {
            const errNormalized = normalizeError(err);
            console.error('Failed to send message:', errNormalized);
        }
    }, [state.isLoading, config.context]);
    const handleRetryLastMessage = useCallback(() => {
        if (instanceRef.current) {
            instanceRef.current.retryLastMessage();
        }
    }, []);
    const handleRetryStream = useCallback(() => {
        if (instanceRef.current && state.error) {
            // Clear error and retry last message
            setState(prev => ({ ...prev, error: null }));
            instanceRef.current.retryLastMessage();
        }
    }, [state.error]);
    // Handle scroll events to detect user intent (per requirements)
    const handleScroll = useCallback(() => {
        if (!chatContainerRef.current)
            return;
        const container = chatContainerRef.current;
        const { scrollTop, scrollHeight, clientHeight } = container;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
        // Bottom threshold ≤50px (per requirements)
        const atBottom = distanceFromBottom <= BOTTOM_THRESHOLD_PX;
        setIsUserAtBottom(atBottom);
        isAtBottomRef.current = atBottom;
        // Detect manual scrolling (150ms debounce per requirements)
        isUserScrollingRef.current = true;
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }
        scrollTimeoutRef.current = setTimeout(() => {
            isUserScrollingRef.current = false;
        }, 150);
    }, []);
    // Set up scroll listener
    useEffect(() => {
        const container = chatContainerRef.current;
        if (!container)
            return;
        container.addEventListener('scroll', handleScroll);
        return () => {
            container.removeEventListener('scroll', handleScroll);
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, [handleScroll]);
    return (<div className={`chat-view ${className}`} style={{
            display: 'flex',
            flexDirection: 'column',
            height: config.ui?.maxHeight || '400px',
            minHeight: 0,
            border: '1px solid var(--border-default)',
            borderRadius: '8px',
            backgroundColor: 'var(--background-secondary)',
            ...style
        }}>
      {/* Messages Container */}
      <div ref={chatContainerRef} style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            padding: '16px',
            scrollBehavior: 'smooth',
            overscrollBehavior: 'contain',
            position: 'relative'
        }}>
        {/* Initial loading overlay */}
        {isInitialLoading && (<div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'var(--panel-overlay-background)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10
            }}>
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '12px'
            }}>
              <div style={{
                display: 'flex',
                gap: '4px'
            }}>
                <div style={{
                width: '8px',
                height: '8px',
                backgroundColor: 'var(--foreground-subtle)',
                borderRadius: '50%',
                animation: 'bounce 1.4s ease-in-out infinite both'
            }}/>
                <div style={{
                width: '8px',
                height: '8px',
                backgroundColor: 'var(--foreground-subtle)',
                borderRadius: '50%',
                animation: 'bounce 1.4s ease-in-out 0.16s infinite both'
            }}/>
                <div style={{
                width: '8px',
                height: '8px',
                backgroundColor: 'var(--foreground-subtle)',
                borderRadius: '50%',
                animation: 'bounce 1.4s ease-in-out 0.32s infinite both'
            }}/>
              </div>
              <div style={{
                fontSize: '14px',
                color: 'var(--foreground-subtle)',
                fontWeight: '500'
            }}>
                Loading chat...
              </div>
            </div>
          </div>)}

        {/* Empty state hint when no messages */}
        {!isInitialLoading && state.messages.length === 0 && !state.isLoading && !state.isStreaming && (<div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%'
            }}>
            <div style={{
                textAlign: 'center',
                padding: '48px'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>💬</div>
              <div style={{
                fontSize: '20px',
                fontWeight: 600,
                color: 'var(--foreground-default)',
                marginBottom: '6px',
                fontFamily: 'Segoe UI, system-ui, sans-serif'
            }}>
                Start a conversation
              </div>
              <div style={{
                fontSize: '16px',
                color: 'var(--foreground-subtle)',
                fontFamily: 'Segoe UI, system-ui, sans-serif'
            }}>
                Type below to chat
              </div>
            </div>
          </div>)}

        <ChatMessageList messages={state.messages} isLoading={state.isLoading} isStreaming={state.isStreaming} onRetryAssistant={handleRetryLastMessage} currentUserName={currentUserProfile?.displayName || currentUserProfile?.name} currentUserEmail={currentUserProfile?.email} currentUserAvatarUrl={currentUserProfile?.avatarUrl ?? null}/>

        {/* Scroll to bottom button - shown when user is not at bottom (per requirements) */}
        {!isUserAtBottom && (<button onClick={() => {
                if (chatContainerRef.current) {
                    chatContainerRef.current.scrollTo({
                        top: chatContainerRef.current.scrollHeight,
                        behavior: 'smooth'
                    });
                }
            }} style={{
                position: 'absolute',
                bottom: '84px',
                right: '20px',
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: 'var(--primary-default)',
                color: 'var(--primary-foreground)',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                transition: 'opacity 0.2s ease',
                zIndex: 10
            }} title="Scroll to bottom">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
            </svg>
          </button>)}

        {/* Error Display */}
        {state.error && (<div style={{
                padding: '12px 16px',
                backgroundColor: 'var(--danger-background)',
                border: '1px solid var(--border-danger)',
                borderRadius: '8px',
                color: 'var(--danger-default)',
                fontSize: '14px'
            }}>
            <div style={{ fontWeight: '600', marginBottom: '4px' }}>
              Error
            </div>
            <div>
              {state.error
                ? formatChatErrorForDisplay(state.error.message, state.error.korvoId, { isAdmin: isAdmin === true })
                : ''}
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <button onClick={handleRetryStream} style={{
                padding: '4px 8px',
                backgroundColor: 'var(--success-default)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '12px',
                cursor: 'pointer'
            }}>
                Retry
              </button>
              <button onClick={() => {
                instanceRef.current?.clearError();
                setState(prev => ({ ...prev, error: null }));
              }} style={{
                padding: '4px 8px',
                backgroundColor: 'var(--danger-default)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '12px',
                cursor: 'pointer'
            }}>
                Dismiss
              </button>
            </div>
          </div>)}
      </div>

      {/* Input Area */}
      <ChatComposer ref={composerRef} disabled={state.isLoading} placeholder={config.ui?.placeholder || 'Type your message...'} onSend={(content) => void handleSendMessage(content)}/>

      <style>{`
        @keyframes bounce {
          0%, 80%, 100% {
            transform: scale(0);
          }
          40% {
            transform: scale(1);
          }
        }
      `}</style>
    </div>);
}
